import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, HeartIcon, EyeIcon, RepeatIcon, ClipboardCheckIcon } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-40px'
  },
  transition: {
    duration: 0.3
  }
};
export function MechanismSection() {
  return <section className="w-full bg-[#0f0529] py-16 md:py-24 px-5" data-id="element-172">
      <div className="max-w-3xl mx-auto" data-id="element-173">
        <motion.div {...fadeIn} data-id="element-174">
          <p className="text-amber-500 text-sm font-medium tracking-wide uppercase mb-4" data-id="element-175">
            El método
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-176">
            No necesitas más disciplina:{' '}
            <span className="text-amber-500" data-id="element-177">necesitas un ANCLA.</span>
          </h2>
          <p className="text-lg text-gray-300 mb-4" data-id="element-178">
            En vez de pelear por ser "disciplinado", tú construyes un ANCLA.
          </p>
          <p className="text-gray-400 mb-12" data-id="element-179">
            Este no es un método para "hacer más".{' '}
            <strong className="text-white" data-id="element-180">
              Es un método para dejar de desaparecer.
            </strong>
          </p>
        </motion.div>

        {/* ANCLA breakdown */}
        <div className="space-y-5" data-id="element-181">
          <motion.div {...fadeIn} transition={{
          duration: 0.3,
          delay: 0.04
        }} data-id="element-182">
            <AnclaStep letter="A" icon={<ZapIcon className="w-5 h-5" data-id="element-184" />} title="Acción mínima" subtitle="5 minutos (nunca cero)" description="Tu primera meta no es cambiar tu vida. Es no hacer cero. Cinco minutos. Eso es todo." data-id="element-183" />
          </motion.div>

          <motion.div {...fadeIn} transition={{
          duration: 0.3,
          delay: 0.06
        }} data-id="element-185">
            <AnclaStep letter="N" icon={<HeartIcon className="w-5 h-5" data-id="element-187" />} title="Nutriente espiritual" subtitle="Un ritual breve que conecta con Dios" description="No es religiosidad vacía. Es un momento real, corto, que ancla tu día a algo más grande que tú." data-id="element-186" />
          </motion.div>

          <motion.div {...fadeIn} transition={{
          duration: 0.3,
          delay: 0.08
        }} data-id="element-188">
            <AnclaStep letter="C" icon={<EyeIcon className="w-5 h-5" data-id="element-190" />} title="Cue (señal visible)" subtitle="Algo físico que te dispara el hábito" description="Sin señal, no hay gatillo. Sin gatillo, dependes de la memoria. Y la memoria falla." data-id="element-189" />
          </motion.div>

          <motion.div {...fadeIn} transition={{
          duration: 0.3,
          delay: 0.1
        }} data-id="element-191">
            <AnclaStep letter="L" icon={<RepeatIcon className="w-5 h-5" data-id="element-193" />} title="Loop" subtitle="Repetición simple (misma hora o mismo gatillo)" description="La consistencia no viene de la motivación. Viene de la repetición en contexto." data-id="element-192" />
          </motion.div>

          <motion.div {...fadeIn} transition={{
          duration: 0.3,
          delay: 0.12
        }} data-id="element-194">
            <AnclaStep letter="A" icon={<ClipboardCheckIcon className="w-5 h-5" data-id="element-196" />} title="Auditoría sin culpa" subtitle="Tracking para ver la verdad sin castigarte" description="Mides para ajustar, no para juzgarte. Si fallas, no abandonas: reinicias." data-id="element-195" />
          </motion.div>
        </div>
      </div>

      <div className="section-divider mt-16 md:mt-24" aria-hidden="true" data-id="element-197" />
    </section>;
}
function AnclaStep({
  letter,
  icon,
  title,
  subtitle,
  description
}: {
  letter: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
}) {
  return <div className="flex gap-4 md:gap-6 bg-white/[0.02] border border-white/5 rounded-xl p-5 md:p-6" data-id="element-198">
      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center" data-id="element-199">
        <span className="text-amber-500 font-bold text-lg" data-id="element-200">{letter}</span>
      </div>
      <div className="flex-1 min-w-0" data-id="element-201">
        <div className="flex items-center gap-2 mb-1" data-id="element-202">
          <span className="text-amber-500/70" data-id="element-203">{icon}</span>
          <h3 className="text-white font-bold" data-id="element-204">{title}</h3>
        </div>
        <p className="text-amber-500/60 text-sm mb-2" data-id="element-205">{subtitle}</p>
        <p className="text-gray-400 text-sm leading-relaxed" data-id="element-206">{description}</p>
      </div>
    </div>;
}